import {useQuery} from "@tanstack/react-query";
import {Loader, Separator, YStack} from "@chayka/ui";
import {BottomSheet, WpImage} from "@/components";
import {trpc} from "@/app/trpc";
import {useLocation, useParams} from "react-router-dom";
import {formatNewsDateLong, separateLinksFromText, transformHtml} from "../utils";

type NewsItemState = {
  id: number;
  title: string;
  content: string;
  mediaId: number;
  date: string;
};

export const NewsDetails = () => {
  const {id} = useParams<{id: string}>();
  const location = useLocation();
  const idNumber = Number(id);
  const stateNews = (location.state as {news?: NewsItemState} | null)?.news;
  const hasValidId = Number.isInteger(idNumber) && idNumber > 0;

  const {data, isFetching} = useQuery({
    ...trpc.news.byId.queryOptions({id: idNumber}),
    enabled: !stateNews && hasValidId,
  });

  const news = stateNews ?? data ?? null;
  const contentRows = news ? separateLinksFromText(news.content) : undefined;
  const keyUsageMap = new Map<string, number>();

  const getPieceKey = (base: string) => {
    const usageCount = keyUsageMap.get(base) ?? 0;
    keyUsageMap.set(base, usageCount + 1);
    return `${base}-${usageCount}`;
  };

  return (
    <BottomSheet fullHeight isOpen={true}>
      <div className="flex w-full flex-col gap-4">
        {isFetching ? (
          <Loader />
        ) : (
          <div className="flex w-full flex-col gap-4">
            {news?.mediaId && (
              <WpImage
                imageId={news.mediaId}
                alt={news.title}
                className="w-full object-cover aspect-square rounded-2xl"
              />
            )}
            <h1 className="text-xl font-bold text-blackout">
              {news ? transformHtml(news.title) : "Новость"}
            </h1>
            {news?.date ? (
              <p className="text-sm text-secondary-gray">{formatNewsDateLong(news.date)}</p>
            ) : null}
            <Separator />
            {news ? (
              <YStack className="gap-1 whitespace-pre-line text-sm leading-relaxed text-blackout">
                {contentRows?.map((row, rowIndex) => {
                  const rowKey = getPieceKey(`row-${rowIndex}`);

                  return (
                    <p key={rowKey}>
                      {row.map((piece) => {
                        if (piece.type === "link") {
                          const key = getPieceKey(`link-${piece.url}-${piece.title}`);
                          return (
                            <a
                              key={key}
                              href={piece.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline"
                            >
                              {piece.title}
                            </a>
                          );
                        }

                        const key = getPieceKey(`text-${piece.text}`);
                        return <span key={key}>{piece.text}</span>;
                      })}
                    </p>
                  );
                })}
              </YStack>
            ) : !isFetching ? (
              <p className="text-sm text-secondary-gray">Новость не найдена.</p>
            ) : null}
          </div>
        )}
      </div>
    </BottomSheet>
  );
};
